import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type HtmlInlineEditState = {
  pendingCount: number;
  isSaving: boolean;
};

const IDLE_STATE: HtmlInlineEditState = {
  pendingCount: 0,
  isSaving: false,
};

type HtmlInlineEditContextValue = HtmlInlineEditState & {
  setAutosaveState: (state: HtmlInlineEditState) => void;
  resetAutosaveState: () => void;
};

const HtmlInlineEditContext = createContext<HtmlInlineEditContextValue | null>(
  null,
);

export function HtmlInlineEditProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<HtmlInlineEditState>(IDLE_STATE);

  const setAutosaveState = useCallback((next: HtmlInlineEditState) => {
    setState(next);
  }, []);

  const resetAutosaveState = useCallback(() => {
    setState(IDLE_STATE);
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      setAutosaveState,
      resetAutosaveState,
    }),
    [state, setAutosaveState, resetAutosaveState],
  );

  return (
    <HtmlInlineEditContext.Provider value={value}>
      {children}
    </HtmlInlineEditContext.Provider>
  );
}

/** Safe defaults when the pane is not HTML or HtmlContent is unmounted. */
export function useHtmlInlineEditState(): HtmlInlineEditState {
  const ctx = useContext(HtmlInlineEditContext);
  return ctx ?? IDLE_STATE;
}

export function useHtmlInlineEditController(): HtmlInlineEditContextValue {
  const ctx = useContext(HtmlInlineEditContext);
  if (!ctx) {
    throw new Error(
      "useHtmlInlineEditController must be used within HtmlInlineEditProvider",
    );
  }
  return ctx;
}
